import Checkbox from '@mui/material/Checkbox';
import { userFields } from '@openx/components-administration/lib/constants.ts';
import type { UserFormData } from '@openx/components-administration/types.ts';
import { RHFAutoCompleteVirtualize } from '@openx/components-core/lib/AutoComplete/RHFAutoCompleteVirtualize.tsx';
import {
  buildParentOrganizationOptions,
  filterParentOrganizationOptions,
} from '@openx/components-organization/lib/components/OrganizationForm/sections/utils.ts';
import type { OrganizationParentAccount } from '@openx/components-organization/lib/types.ts';
import { useOrganizationContext } from '@openx/components-organization/lib/utils/context/OrganizationContext.tsx';
import { OxsUserRole } from '@openx/types/user.ts';
import { useCallback, useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { isAdminRoleAllowed } from '../../utils.ts';

type OrganizationFieldProps = {
  isEdit?: boolean;
};

const isOptionEqualToValue = (option: OrganizationParentAccount, value: OrganizationParentAccount | string) =>
  typeof value !== 'string' && option.id === value.id;

export const OrganizationField = ({ isEdit }: OrganizationFieldProps) => {
  const { t } = useTranslation();
  const { useOrganizationsListFetch } = useOrganizationContext();
  const { loading, organizations, error } = useOrganizationsListFetch({ variables: {}, skip: isEdit });
  const parentOrganizationOptions = useMemo(() => buildParentOrganizationOptions(organizations), [organizations]);

  const {
    field: { value: selectedAccount, onChange: onAccountChange },
  } = useController<UserFormData, typeof userFields.ACCOUNT>({ name: userFields.ACCOUNT });
  const { getValues, setValue } = useFormContext<UserFormData>();

  const syncRoleWithAccount = useCallback(
    (account: OrganizationParentAccount | null) => {
      // root organization allows only admin role, whereas it is not allowed for any other organization
      if (isAdminRoleAllowed(account)) {
        setValue(userFields.ROLE, OxsUserRole.ADMIN, { shouldDirty: true, shouldValidate: true });
        return;
      }

      if (getValues(userFields.ROLE) === OxsUserRole.ADMIN) {
        setValue(userFields.ROLE, null, { shouldDirty: true, shouldValidate: true });
      }
    },
    [getValues, setValue]
  );

  const handleAccountChange = useCallback(
    (_event: unknown, option: OrganizationParentAccount | null) => {
      if (!option || (selectedAccount && option.id === selectedAccount.id)) {
        onAccountChange(null);
        syncRoleWithAccount(null);
        return;
      }

      onAccountChange(option);
      syncRoleWithAccount(option);
    },
    [syncRoleWithAccount, onAccountChange, selectedAccount]
  );

  const isOrganizationOptionChecked = (option: OrganizationParentAccount, selectedAccount: UserFormData['account']) => {
    if (!selectedAccount) return false;

    // Option is checked when it is the selected organization or one of its descendants,
    // as the user inherits access to the whole subtree of the selected organization
    return (
      option.id === selectedAccount.id ||
      (option.parentOrganization ?? []).some(ancestor => ancestor.id === selectedAccount.id)
    );
  };

  if (error) throw new Error('Organization fetch error');

  return (
    <RHFAutoCompleteVirtualize
      name={userFields.ACCOUNT}
      textFieldProps={{
        label: t('Organization'),
        margin: 'dense',
        placeholder: t('[ Select Organization ]'),
      }}
      options={parentOrganizationOptions}
      getOptionLabel={option => option.name}
      isOptionEqualToValue={isOptionEqualToValue}
      onChange={handleAccountChange}
      filterOptions={filterParentOrganizationOptions}
      sx={{ mb: 1 }}
      data-test="organization"
      loading={loading}
      disabled={isEdit}
      disableCloseOnSelect
      renderOptions={{
        getOptionValue: optionId => optionId.name,
        getPaddingLevel: optionId => optionId.depth ?? 1,
        childrenLocation: 'above',
        // Checkbox is only a visual indicator of the selected organization and its inherited sub-organizations,
        // the field remains a single select and the click is handled by the option itself
        renderChildren: option => (
          <Checkbox
            checked={isOrganizationOptionChecked(option, selectedAccount)}
            size="medium"
            tabIndex={-1}
            disableRipple
            sx={{ p: 0, mr: 1, mb: 0.5, pointerEvents: 'none' }}
            slotProps={{ input: { 'aria-hidden': true } }}
            data-test="organization-option-checkbox"
          />
        ),
      }}
    />
  );
};
